/**
 * Centralized Project Management API Service Layer
 * All PM endpoints are prefixed with /pm-service/api/v1
 */
import apiClient from "utils/apiClient";
import { PM_SERVICE_BASE_URL, ACTOR_ID_HEADER_KEY } from "constants";

const BASE = `${PM_SERVICE_BASE_URL}/api/v1`;

// ─── Helper: build actor header ───────────────────────────────────────────────
const actorHeaders = (actorId) =>
  actorId ? { [ACTOR_ID_HEADER_KEY]: actorId } : {};

const userHeaders = (userId, userName) => {
  const h = {};
  if (userId) h["X-User-Id"] = userId;
  if (userName) h["X-User-Name"] = userName;
  return h;
};

// ═══════════════════════════════════════════════════════════════════════════════
// PROJECTS
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * List / search projects (paginated).
 * @param {Object} params - { page, size, search, tags, logic, id, code, name, description, ownerName }
 * @returns {Promise<Object>} Page<ProjectDTO>
 */
export const listProjects = (params = {}) =>
  apiClient.get(`${BASE}/projects`, { params }).then((r) => r.data);

/**
 * Get a single project.
 * @param {string} projectId
 * @returns {Promise<Object>} ProjectDTO
 */
export const getProject = (projectId) =>
  apiClient.get(`${BASE}/projects/${projectId}`).then((r) => r.data);

/**
 * Create a project.
 * @param {Object} data - CreateProjectRequest
 * @param {string} [actorId]
 * @returns {Promise<Object>} ProjectDTO
 */
export const createProject = (data, actorId) =>
  apiClient
    .post(`${BASE}/projects`, data, { headers: actorHeaders(actorId) })
    .then((r) => r.data);

/**
 * Update a project.
 * @param {string} projectId
 * @param {Object} data - UpdateProjectRequest
 * @param {string} [actorId]
 * @returns {Promise<Object>} ProjectDTO
 */
export const updateProject = (projectId, data, actorId) =>
  apiClient
    .put(`${BASE}/projects/${projectId}`, data, {
      headers: actorHeaders(actorId),
    })
    .then((r) => r.data);

/**
 * Delete a project.
 * @param {string} projectId
 * @param {string} [actorId]
 */
export const deleteProject = (projectId, actorId) =>
  apiClient.delete(`${BASE}/projects/${projectId}`, {
    headers: actorHeaders(actorId),
  });

/**
 * Get actual points for a project.
 * @param {string} projectId
 * @returns {Promise<number>}
 */
export const getProjectPoints = (projectId) =>
  apiClient.get(`${BASE}/projects/${projectId}/points`).then((r) => r.data);

/**
 * Add tags to a project.
 * @param {string} projectId
 * @param {number[]} tagIds
 * @param {string} [actorId]
 */
export const addProjectTags = (projectId, tagIds, actorId) =>
  apiClient.post(`${BASE}/projects/${projectId}/tags`, tagIds, {
    headers: actorHeaders(actorId),
  });

/**
 * Remove tags from a project.
 * @param {string} projectId
 * @param {number[]} tagIds
 * @param {string} [actorId]
 */
export const removeProjectTags = (projectId, tagIds, actorId) =>
  apiClient.delete(`${BASE}/projects/${projectId}/tags`, {
    params: { tagIds: tagIds.join(",") },
    headers: actorHeaders(actorId),
  });

// ═══════════════════════════════════════════════════════════════════════════════
// ITERATIONS
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * List / search iterations for a project (paginated).
 * @param {string} projectId
 * @param {Object} params - { page, size, sort, search, tags, logic, id, key, name, type }
 * @returns {Promise<Object>} Page<IterationDTO>
 */
export const listIterations = (projectId, params = {}) =>
  apiClient
    .get(`${BASE}/projects/${projectId}/iterations`, { params })
    .then((r) => r.data);

/**
 * Create an iteration.
 * @param {string} projectId
 * @param {Object} data - CreateIterationRequest
 * @returns {Promise<Object>} IterationDTO
 */
export const createIteration = (projectId, data) =>
  apiClient
    .post(`${BASE}/projects/${projectId}/iterations`, data)
    .then((r) => r.data);

/**
 * Update an iteration.
 * @param {string} projectId
 * @param {string} iterationId
 * @param {Object} data - UpdateIterationRequest
 * @returns {Promise<Object>} IterationDTO
 */
export const updateIteration = (projectId, iterationId, data) =>
  apiClient
    .put(`${BASE}/projects/${projectId}/iterations/${iterationId}`, data)
    .then((r) => r.data);

/**
 * Delete an iteration.
 * @param {string} projectId
 * @param {string} iterationId
 */
export const deleteIteration = (projectId, iterationId) =>
  apiClient.delete(
    `${BASE}/projects/${projectId}/iterations/${iterationId}`
  );

/**
 * Get actual points for an iteration.
 * @param {string} projectId
 * @param {string} iterationId
 * @returns {Promise<number>}
 */
export const getIterationPoints = (projectId, iterationId) =>
  apiClient
    .get(`${BASE}/projects/${projectId}/iterations/${iterationId}/points`)
    .then((r) => r.data);

/**
 * Add tags to an iteration.
 * @param {string} projectId
 * @param {string} iterationId
 * @param {number[]} tagIds
 */
export const addIterationTags = (projectId, iterationId, tagIds) =>
  apiClient.post(
    `${BASE}/projects/${projectId}/iterations/${iterationId}/tags`,
    tagIds
  );

/**
 * Remove tags from an iteration.
 * @param {string} projectId
 * @param {string} iterationId
 * @param {number[]} tagIds
 */
export const removeIterationTags = (projectId, iterationId, tagIds) =>
  apiClient.delete(
    `${BASE}/projects/${projectId}/iterations/${iterationId}/tags`,
    { params: { tagIds: tagIds.join(",") } }
  );

// ═══════════════════════════════════════════════════════════════════════════════
// TASKS
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * List / search tasks for a project (paginated).
 * @param {string} projectId
 * @param {Object} params - { page, size, sort, search, tags, logic, status, iterationId, assignmentStatus, id, title, description, assigneeId, createdByUserId }
 * @returns {Promise<Object>} Page<TaskDTO>
 */
export const listTasks = (projectId, params = {}) =>
  apiClient
    .get(`${BASE}/projects/${projectId}/tasks`, { params })
    .then((r) => r.data);

/**
 * Get a single task.
 * @param {string} projectId
 * @param {string} taskId
 * @returns {Promise<Object>} TaskDTO
 */
export const getTask = (projectId, taskId) =>
  apiClient
    .get(`${BASE}/projects/${projectId}/tasks/${taskId}`)
    .then((r) => r.data);

/**
 * Create a task.
 * @param {string} projectId
 * @param {Object} data - CreateTaskRequest
 * @param {string} [actorId]
 * @returns {Promise<Object>} TaskDTO
 */
export const createTask = (projectId, data, actorId) =>
  apiClient
    .post(`${BASE}/projects/${projectId}/tasks`, data, {
      headers: actorHeaders(actorId),
    })
    .then((r) => r.data);

/**
 * Update a task.
 * @param {string} projectId
 * @param {string} taskId
 * @param {Object} data - UpdateTaskRequest
 * @param {string} [actorId]
 * @returns {Promise<Object>} TaskDTO
 */
export const updateTask = (projectId, taskId, data, actorId) =>
  apiClient
    .put(`${BASE}/projects/${projectId}/tasks/${taskId}`, data, {
      headers: actorHeaders(actorId),
    })
    .then((r) => r.data);

/**
 * Delete a task.
 * @param {string} projectId
 * @param {string} taskId
 */
export const deleteTask = (projectId, taskId) =>
  apiClient.delete(`${BASE}/projects/${projectId}/tasks/${taskId}`);

/**
 * Add tags to a task.
 * @param {string} projectId
 * @param {string} taskId
 * @param {number[]} tagIds
 */
export const addTaskTags = (projectId, taskId, tagIds) =>
  apiClient.post(
    `${BASE}/projects/${projectId}/tasks/${taskId}/tags`,
    tagIds
  );

/**
 * Remove tags from a task.
 * @param {string} projectId
 * @param {string} taskId
 * @param {number[]} tagIds
 */
export const removeTaskTags = (projectId, taskId, tagIds) =>
  apiClient.delete(
    `${BASE}/projects/${projectId}/tasks/${taskId}/tags`,
    { params: { tagIds: tagIds.join(",") } }
  );

// ═══════════════════════════════════════════════════════════════════════════════
// TROUBLESHOOTING GUIDES
// ═══════════════════════════════════════════════════════════════════════════════

export const listTroubleshootingGuides = (params = {}) =>
  apiClient
    .get(`${BASE}/troubleshooting-guides`, { params })
    .then((r) => r.data);

export const getTroubleshootingGuide = (guideId) =>
  apiClient
    .get(`${BASE}/troubleshooting-guides/${guideId}`)
    .then((r) => r.data);

export const createTroubleshootingGuide = (data, actorId) =>
  apiClient
    .post(`${BASE}/troubleshooting-guides`, data, {
      headers: actorHeaders(actorId),
    })
    .then((r) => r.data);

export const updateTroubleshootingGuide = (guideId, data, actorId) =>
  apiClient
    .put(`${BASE}/troubleshooting-guides/${guideId}`, data, {
      headers: actorHeaders(actorId),
    })
    .then((r) => r.data);

export const deleteTroubleshootingGuide = (guideId, actorId) =>
  apiClient.delete(`${BASE}/troubleshooting-guides/${guideId}`, {
    headers: actorHeaders(actorId),
  });

// ═══════════════════════════════════════════════════════════════════════════════
// INCIDENTS
// ═══════════════════════════════════════════════════════════════════════════════

export const listIncidents = (params = {}) =>
  apiClient.get(`${BASE}/incidents`, { params }).then((r) => r.data);

export const getIncident = (incidentId) =>
  apiClient.get(`${BASE}/incidents/${incidentId}`).then((r) => r.data);

export const createIncident = (data, actorId) =>
  apiClient
    .post(`${BASE}/incidents`, data, { headers: actorHeaders(actorId) })
    .then((r) => r.data);

export const updateIncident = (incidentId, data, actorId) =>
  apiClient
    .put(`${BASE}/incidents/${incidentId}`, data, {
      headers: actorHeaders(actorId),
    })
    .then((r) => r.data);

export const deleteIncident = (incidentId, actorId) =>
  apiClient.delete(`${BASE}/incidents/${incidentId}`, {
    headers: actorHeaders(actorId),
  });

// ═══════════════════════════════════════════════════════════════════════════════
// TICKETS
// ═══════════════════════════════════════════════════════════════════════════════

export const listTickets = (params = {}) =>
  apiClient.get(`${BASE}/tickets`, { params }).then((r) => r.data);

export const getTicket = (ticketId) =>
  apiClient.get(`${BASE}/tickets/${ticketId}`).then((r) => r.data);

export const createTicket = (data, actorId) =>
  apiClient
    .post(`${BASE}/tickets`, data, { headers: actorHeaders(actorId) })
    .then((r) => r.data);

export const updateTicket = (ticketId, data, actorId) =>
  apiClient
    .put(`${BASE}/tickets/${ticketId}`, data, {
      headers: actorHeaders(actorId),
    })
    .then((r) => r.data);

export const deleteTicket = (ticketId, actorId) =>
  apiClient.delete(`${BASE}/tickets/${ticketId}`, {
    headers: actorHeaders(actorId),
  });

const operationCommentUrl = (resourcePath, recordId, commentId) =>
  `${BASE}/${resourcePath}/${recordId}/comments${commentId ? `/${commentId}` : ""}`;

const normalizeCommentPage = (data) =>
  Array.isArray(data?.content) ? data.content : (Array.isArray(data) ? data : []);

export const listOperationComments = (resourcePath, recordId, params = {}) =>
  apiClient
    .get(operationCommentUrl(resourcePath, recordId), {
      params: { sort: "createdAt,desc", page: 0, ...params },
    })
    .then((r) => normalizeCommentPage(r.data));

export const addOperationComment = (resourcePath, recordId, data, userId, userName) =>
  apiClient
    .post(operationCommentUrl(resourcePath, recordId), data, {
      headers: userHeaders(userId, userName),
    })
    .then((r) => r.data);

export const updateOperationComment = (resourcePath, recordId, commentId, data, userId, userName) =>
  apiClient
    .put(operationCommentUrl(resourcePath, recordId, commentId), data, {
      headers: userHeaders(userId, userName),
    })
    .then((r) => r.data);

export const deleteOperationComment = (resourcePath, recordId, commentId) =>
  apiClient.delete(operationCommentUrl(resourcePath, recordId, commentId));

const operationAttachmentUrl = (resourcePath, recordId, attachmentId) =>
  `${BASE}/${resourcePath}/${recordId}/attachments${attachmentId ? `/${attachmentId}` : ""}`;

export const listOperationAttachments = (resourcePath, recordId) =>
  apiClient.get(operationAttachmentUrl(resourcePath, recordId)).then((r) => r.data || []);

export const addOperationAttachments = (resourcePath, recordId, data, actorId) =>
  apiClient
    .post(operationAttachmentUrl(resourcePath, recordId), data, {
      headers: actorHeaders(actorId),
    })
    .then((r) => r.data || []);

export const patchOperationAttachment = (resourcePath, recordId, attachmentId, data, actorId) =>
  apiClient
    .patch(operationAttachmentUrl(resourcePath, recordId, attachmentId), data, {
      headers: actorHeaders(actorId),
    })
    .then((r) => r.data);

export const deleteOperationAttachment = (resourcePath, recordId, attachmentId, actorId) =>
  apiClient.delete(operationAttachmentUrl(resourcePath, recordId, attachmentId), {
    headers: actorHeaders(actorId),
  });

// ═══════════════════════════════════════════════════════════════════════════════
// DOCS
// ═══════════════════════════════════════════════════════════════════════════════

export const listDocs = (params = {}) =>
  apiClient.get(`${BASE}/docs`, { params }).then((r) => r.data);

export const getDoc = (docId) =>
  apiClient.get(`${BASE}/docs/${docId}`).then((r) => r.data);

export const listDocTree = () =>
  apiClient.get(`${BASE}/docs/tree`).then((r) => r.data);

export const listDocMenuTree = () =>
  apiClient.get(`${BASE}/docs/menu-tree`).then((r) => r.data);

export const createDoc = (data, actorId) =>
  apiClient
    .post(`${BASE}/docs`, data, { headers: actorHeaders(actorId) })
    .then((r) => r.data);

export const updateDoc = (docId, data, actorId) =>
  apiClient
    .put(`${BASE}/docs/${docId}`, data, { headers: actorHeaders(actorId) })
    .then((r) => r.data);

export const updateDocProperties = (docId, updates = [], actorId) =>
  apiClient
    .patch(`${BASE}/docs/${docId}/props`, updates, {
      headers: actorHeaders(actorId),
    })
    .then((r) => r.data);

export const deleteDoc = (docId, actorId) =>
  apiClient.delete(`${BASE}/docs/${docId}`, {
    headers: actorHeaders(actorId),
  });

/**
 * Generate a task draft using AI.
 * POST /api/v1/ai/tasks/generate
 * @param {string} projectId
 * @param {string} prompt
 * @param {Object} [context] - { product, module, taskType }
 * @param {string} [actorId]
 * @returns {Promise<Object>} AiTaskDraftResponse
 */
export const generateTaskWithAI = (projectId, prompt, context, actorId) =>
  apiClient
    .post(`${BASE}/ai/tasks/generate`,
      { projectId, prompt, context },
      { headers: actorHeaders(actorId) }
    )
    .then((r) => r.data);

/**
 * Get AI generation quota for the current workspace.
 * GET /api/v1/ai/tasks/quota
 * @param {string} [actorId]
 * @returns {Promise<Object>} { used, limit, periodStart, periodEnd }
 */
export const getAiGenerationQuota = (actorId) =>
  apiClient
    .get(`${BASE}/ai/tasks/quota`, { headers: actorHeaders(actorId) })
    .then((r) => r.data);

/**
 * Get AI generation usage for a specific workspace (mirrors olreadia pattern).
 * GET /api/v1/usage/ai-generations?workspaceId=X
 * @param {string} workspaceId
 * @returns {Promise<Object>} { used, limit, periodStart, periodEnd }
 */
export const getAiGenerationUsage = (workspaceId) =>
  apiClient
    .get(`${BASE}/usage/ai-generations`, { params: { workspaceId } })
    .then((r) => r.data);

/**
 * Bulk import tasks from a document.
 * @param {string} projectId
 * @param {string} iterationId
 * @param {File} file
 * @param {string} [actorId]
 * @returns {Promise<Object>} BulkImportResponse
 */
export const bulkImportTasks = (projectId, iterationId, file, actorId) => {
  const formData = new FormData();
  formData.append("file", file);
  return apiClient
    .post(
      `${BASE}/projects/${projectId}/tasks/iterations/${iterationId}/bulk-import`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
          ...actorHeaders(actorId),
        },
      }
    )
    .then((r) => r.data);
};

/**
 * Bulk import tasks from JSON payload
 * @param {string} projectId
 * @param {string} iterationId
 * @param {Array} tasks - Array of task objects
 * @param {string} [actorId]
 * @returns {Promise<Object>} BulkImportResponse
 */
export const bulkImportTasksJson = (projectId, iterationId, tasks, actorId) => {
  return apiClient
    .post(
      `${BASE}/projects/${projectId}/tasks/iterations/${iterationId}/bulk-import/json`,
      tasks,
      {
        headers: {
          "Content-Type": "application/json",
          ...actorHeaders(actorId),
        },
      }
    )
    .then((r) => r.data);
};

// ═══════════════════════════════════════════════════════════════════════════════
// COMMENTS
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * List comments for a task.
 * @param {string} projectId
 * @param {string} taskId
 * @returns {Promise<Object[]>} CommentDTO[]
 */
export const listComments = (projectId, taskId) =>
  apiClient
    .get(`${BASE}/projects/${projectId}/tasks/${taskId}/comments`)
    .then((r) => r.data);

/**
 * Get a single comment.
 * @param {string} projectId
 * @param {string} taskId
 * @param {string} commentId
 * @returns {Promise<Object>} CommentDTO
 */
export const getComment = (projectId, taskId, commentId) =>
  apiClient
    .get(
      `${BASE}/projects/${projectId}/tasks/${taskId}/comments/${commentId}`
    )
    .then((r) => r.data);

/**
 * Add a comment to a task.
 * @param {string} projectId
 * @param {string} taskId
 * @param {Object} data - CreateCommentRequest
 * @param {string} userId
 * @param {string} userName
 * @returns {Promise<Object>} CommentDTO
 */
export const addComment = (projectId, taskId, data, userId, userName) =>
  apiClient
    .post(
      `${BASE}/projects/${projectId}/tasks/${taskId}/comments`,
      data,
      { headers: userHeaders(userId, userName) }
    )
    .then((r) => r.data);

/**
 * Update a comment.
 * @param {string} projectId
 * @param {string} taskId
 * @param {string} commentId
 * @param {Object} data - UpdateCommentRequest
 * @param {string} userId
 * @param {string} userName
 * @returns {Promise<Object>} CommentDTO
 */
export const updateComment = (
  projectId,
  taskId,
  commentId,
  data,
  userId,
  userName
) =>
  apiClient
    .put(
      `${BASE}/projects/${projectId}/tasks/${taskId}/comments/${commentId}`,
      data,
      { headers: userHeaders(userId, userName) }
    )
    .then((r) => r.data);

/**
 * Delete a comment.
 * @param {string} projectId
 * @param {string} taskId
 * @param {string} commentId
 */
export const deleteComment = (projectId, taskId, commentId) =>
  apiClient.delete(
    `${BASE}/projects/${projectId}/tasks/${taskId}/comments/${commentId}`
  );

// ═══════════════════════════════════════════════════════════════════════════════
// LINKS
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Get links for a task.
 * @param {string} taskId
 * @returns {Promise<Object[]>} LinkDTO[]
 */
export const listLinks = (taskId) =>
  apiClient
    .get(`${BASE}/links/tasks/${taskId}/links`)
    .then((r) => (Array.isArray(r.data) ? r.data : r.data?.data || []));

/**
 * Get a single link.
 * @param {string} taskId
 * @param {string} linkId
 * @returns {Promise<Object>} LinkDTO
 */
export const getLink = (taskId, linkId) =>
  apiClient
    .get(`${BASE}/links/tasks/${taskId}/links/${linkId}`)
    .then((r) => r.data);

/**
 * Add link(s) to a task.
 * @param {string} taskId
 * @param {Object[]} links - CreateLinkRequest[]
 * @param {string} [actorId]
 * @returns {Promise<Object[]>} LinkDTO[]
 */
export const addLinks = (taskId, links, actorId) =>
  apiClient
    .post(`${BASE}/links/tasks/${taskId}/links`, links, {
      headers: actorHeaders(actorId),
    })
    .then((r) => r.data);

/**
 * Update a link.
 * @param {string} taskId
 * @param {string} linkId
 * @param {Object} data - CreateLinkRequest
 * @param {string} [actorId]
 * @returns {Promise<Object>} LinkDTO
 */
export const updateLink = (taskId, linkId, data, actorId) =>
  apiClient
    .put(`${BASE}/links/tasks/${taskId}/links/${linkId}`, data, {
      headers: actorHeaders(actorId),
    })
    .then((r) => r.data);

/**
 * Delete a link.
 * @param {string} taskId
 * @param {string} linkId
 * @param {string} [actorId]
 */
export const deleteLink = (taskId, linkId, actorId) =>
  apiClient.delete(`${BASE}/links/tasks/${taskId}/links/${linkId}`, {
    headers: actorHeaders(actorId),
  });

// ═══════════════════════════════════════════════════════════════════════════════
// TAGS
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * List all tags.
 * @returns {Promise<Object[]>} TagDTO[]
 */
export const listTags = () =>
  apiClient.get(`${BASE}/tags`).then((r) => r.data);

/**
 * Get a tag by id.
 * @param {number} id
 * @returns {Promise<Object>} TagDTO
 */
export const getTag = (id) =>
  apiClient.get(`${BASE}/tags/${id}`).then((r) => r.data);

/**
 * Create a tag.
 * @param {Object} data - CreateTagRequest
 * @returns {Promise<Object>} TagDTO
 */
export const createTag = (data) =>
  apiClient.post(`${BASE}/tags`, data).then((r) => r.data);

/**
 * Update a tag.
 * @param {number} id
 * @param {Object} data - UpdateTagRequest
 * @returns {Promise<Object>} TagDTO
 */
export const updateTag = (id, data) =>
  apiClient.put(`${BASE}/tags/${id}`, data).then((r) => r.data);

/**
 * Delete a tag.
 * @param {number} id
 */
export const deleteTag = (id) => apiClient.delete(`${BASE}/tags/${id}`);

// ═══════════════════════════════════════════════════════════════════════════════
// PROJECT MEMBERS & ROLES
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * List project members.
 * @param {string} projectId
 * @returns {Promise<Object[]>} ProjectMemberDTO[]
 */
export const listProjectMembers = (projectId) =>
  apiClient.get(`${BASE}/projects/${projectId}/members`).then((r) => r.data);

/**
 * Add a member to a project.
 * @param {string} projectId
 * @param {Object} data - { userId, roleId }
 * @param {string} [actorId]
 * @returns {Promise<Object>} ProjectMemberDTO
 */
export const addProjectMember = (projectId, data, actorId) =>
  apiClient.post(`${BASE}/projects/${projectId}/members`, data, {
    headers: actorHeaders(actorId),
  }).then((r) => r.data);

/**
 * Update a project member's role.
 * @param {string} projectId
 * @param {string} memberId
 * @param {Object} data - { roleId }
 * @param {string} [actorId]
 * @returns {Promise<Object>} ProjectMemberDTO
 */
export const updateProjectMemberRole = (projectId, memberId, data, actorId) =>
  apiClient.put(`${BASE}/projects/${projectId}/members/${memberId}`, data, {
    headers: actorHeaders(actorId),
  }).then((r) => r.data);

/**
 * Remove a member from a project.
 * @param {string} projectId
 * @param {string} memberId
 * @param {string} [actorId]
 */
export const removeProjectMember = (projectId, memberId, actorId) =>
  apiClient.delete(`${BASE}/projects/${projectId}/members/${memberId}`, {
    headers: actorHeaders(actorId),
  });

/**
 * Transfer project admin to another member.
 * @param {string} projectId
 * @param {string} newAdminId
 * @param {string} [actorId]
 * @returns {Promise<Object>} ProjectDTO
 */
export const transferProjectAdmin = (projectId, newAdminId, actorId) =>
  apiClient.post(`${BASE}/projects/${projectId}/transfer-admin`, { newAdminId }, {
    headers: actorHeaders(actorId),
  }).then((r) => r.data);

/**
 * List project roles.
 * @param {string} projectId
 * @returns {Promise<Object[]>} ProjectRoleDTO[]
 */
export const listProjectRoles = (projectId) =>
  apiClient.get(`${BASE}/projects/${projectId}/roles`).then((r) => r.data);

/**
 * Create a project role.
 * @param {string} projectId
 * @param {Object} data - CreateProjectRoleRequest
 * @param {string} [actorId]
 * @returns {Promise<Object>} ProjectRoleDTO
 */
export const createProjectRole = (projectId, data, actorId) =>
  apiClient.post(`${BASE}/projects/${projectId}/roles`, data, {
    headers: actorHeaders(actorId),
  }).then((r) => r.data);

/**
 * Update a project role.
 * @param {string} projectId
 * @param {string} roleId
 * @param {Object} data - UpdateProjectRoleRequest
 * @param {string} [actorId]
 * @returns {Promise<Object>} ProjectRoleDTO
 */
export const updateProjectRole = (projectId, roleId, data, actorId) =>
  apiClient.put(`${BASE}/projects/${projectId}/roles/${roleId}`, data, {
    headers: actorHeaders(actorId),
  }).then((r) => r.data);

/**
 * Delete a project role.
 * @param {string} projectId
 * @param {string} roleId
 * @param {string} [actorId]
 */
export const deleteProjectRole = (projectId, roleId, actorId) =>
  apiClient.delete(`${BASE}/projects/${projectId}/roles/${roleId}`, {
    headers: actorHeaders(actorId),
  });
